import React, { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import type { AxiosProgressEvent } from 'axios';
import StepCard from '../../../courses/wizard/shared/StepCard';
import DropZone from '../../../courses/wizard/shared/DropZone';
import { usePlanFeatures } from '../../../../hooks/usePlanFeatures';
import { useUpdateProgram } from '../../../../services/programs';
import { uploadFile } from '../../../../services/uploads';
import type { CertificationType } from '../../../../types/program';
import { palette } from '@/styles/figma-tokens';

const MAX_PDF_SIZE_MB = 20;
const ALLOWED_PDF_TYPES = ['application/pdf'];

interface ProgramCertificationStepProps {
  programId: string;
  /** Pre-populated values for edit mode. */
  initialCertificationType?: CertificationType | null;
  initialCertificateFileKey?: string | null;
  disabled?: boolean;
  onSaved: () => void;
  /** Stagger delay forwarded to StepCard (seconds). */
  entranceDelay?: number;
}

// ─── Choice-card icons ────────────────────────────────────────────────────────

function CertNoneIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <rect x="6" y="10" width="36" height="28" rx="4" stroke={palette.grey['500']} strokeWidth="2.5" fill="none" />
      <path d="M12 18h24M12 24h16" stroke={palette.grey['500']} strokeWidth="2" strokeLinecap="round" />
      <circle cx="34" cy="34" r="7" fill="#fff" stroke={palette.grey['500']} strokeWidth="2" />
      <path d="M31 31l6 6M37 31l-6 6" stroke={palette.error['500']} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function CertAutoIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <rect x="6" y="10" width="36" height="28" rx="4" style={{ stroke: 'rgb(var(--color-primary))' }} strokeWidth="2.5" fill="none" />
      <path d="M12 18h24M12 24h16" style={{ stroke: 'rgb(var(--color-primary))' }} strokeWidth="2" strokeLinecap="round" />
      <circle cx="34" cy="34" r="7" fill="rgb(var(--color-primary) / 0.08)" style={{ stroke: 'rgb(var(--color-primary))' }} strokeWidth="2" />
      <path
        d="M34 28.5v2M34 37.5v2M29.5 34h2M36.5 34h2M31.1 30.1l1.4 1.4M36.5 35.5l1.4 1.4M31.1 37.9l1.4-1.4M36.5 32.5l1.4-1.4"
        style={{ stroke: 'rgb(var(--color-primary))' }}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CertUploadIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <rect x="6" y="10" width="36" height="28" rx="4" stroke={palette.grey['500']} strokeWidth="2.5" fill="none" />
      <path d="M12 18h24M12 24h12" stroke={palette.grey['500']} strokeWidth="2" strokeLinecap="round" />
      <circle cx="34" cy="34" r="7" fill="rgb(var(--color-primary) / 0.08)" style={{ stroke: 'rgb(var(--color-primary))' }} strokeWidth="2" />
      <path d="M34 37.5v-5M31.5 34.5l2.5-2.5 2.5 2.5" style={{ stroke: 'rgb(var(--color-primary))' }} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface ChoiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
  /** Plan-locked: visible (staff may see the offer) but not selectable. */
  lockedPlanTag?: string;
}

function ChoiceCard({ icon, title, description, selected, onClick, lockedPlanTag }: ChoiceCardProps) {
  const locked = Boolean(lockedPlanTag);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={locked}
      title={locked ? `Available from the ${lockedPlanTag} plan` : undefined}
      aria-pressed={selected}
      className="group active:scale-[0.99] transition-[border-color,background-color,transform] duration-150"
      style={{
        flex: 1,
        minWidth: '180px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: '24px',
        borderRadius: '16px',
        border: `2px solid ${selected ? 'rgb(var(--color-primary))' : 'rgba(189,188,201,0.3)'}`,
        background: selected ? 'rgb(var(--color-primary) / 0.04)' : '#fff',
        cursor: locked ? 'not-allowed' : 'pointer',
        opacity: locked ? 0.6 : 1,
        outline: 'none',
      }}
    >
      <div style={{ marginBottom: '16px' }}>{icon}</div>
      <p
        style={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: '22px',
          color: palette.grey['800'],
          margin: '0 0 6px',
        }}
      >
        {title}
        {locked && (
          <span
            className="text-primary-600"
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {lockedPlanTag} plan
          </span>
        )}
      </p>
      <p
        style={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: '18px',
          color: palette.grey['600'],
          margin: 0,
        }}
      >
        {description}
      </p>
    </button>
  );
}

// ─── Step ─────────────────────────────────────────────────────────────────────

export default function ProgramCertificationStep({
  programId,
  initialCertificationType,
  initialCertificateFileKey,
  disabled = false,
  onSaved,
  entranceDelay,
}: ProgramCertificationStepProps) {
  const { certificates: certificatesAllowed, customCertificates: customAllowed } =
    usePlanFeatures();
  const [choice, setChoice] = useState<CertificationType>(initialCertificationType ?? 'none');
  const [pdfKey, setPdfKey] = useState<string | null>(initialCertificateFileKey ?? null);
  const [pdfName, setPdfName] = useState<string | null>(
    initialCertificateFileKey ? 'Certificate template.pdf' : null
  );
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const updateProgram = useUpdateProgram();

  const handlePdfSelected = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) return;
      if (!ALLOWED_PDF_TYPES.includes(file.type)) {
        setUploadError('Only PDF files are allowed');
        return;
      }
      setUploadError(null);
      setPdfName(file.name);
      setUploading(true);
      setUploadProgress(0);
      try {
        const result = await uploadFile(
          { file, resource_type: 'program_certificate', resource_id: programId },
          {
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
              const pct = Math.round(
                (progressEvent.loaded * 100) / (progressEvent.total || 100)
              );
              setUploadProgress(pct);
            },
          }
        );
        setPdfKey(result.key);
        setUploading(false);
        setUploadProgress(100);
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? 'Upload failed. Please try again.';
        setUploadError(message);
        setUploading(false);
        setPdfName(null);
      }
    },
    [programId]
  );

  const handleRemovePdf = () => {
    setPdfKey(null);
    setPdfName(null);
    setUploadError(null);
    setUploadProgress(0);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProgram.mutateAsync({
        programId,
        data: {
          certification_type: choice,
          certificate_file_key: choice === 'custom_upload' ? pdfKey : null,
        },
      });
      setSaved(true);
      onSaved();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to save certification. Please try again.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const summary = saved
    ? choice === 'auto_generated'
      ? 'Auto-generated certificate'
      : choice === 'custom_upload'
        ? `Custom upload${pdfName ? ` · ${pdfName}` : ''}`
        : 'No certificate'
    : undefined;

  return (
    <StepCard
      stepNumber={3}
      title="Certification"
      disabled={disabled}
      onSave={handleSave}
      saving={saving}
      saved={saved}
      summary={summary}
      entranceDelay={entranceDelay}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <ChoiceCard
            icon={<CertNoneIcon />}
            title="No certification"
            description="There will be no certification for this program"
            selected={choice === 'none'}
            onClick={() => setChoice('none')}
          />
          <ChoiceCard
            icon={<CertAutoIcon />}
            title="Auto-Generated Certificate"
            description="We will create the certificate once the learner completes the program"
            selected={choice === 'auto_generated'}
            onClick={() => setChoice('auto_generated')}
            lockedPlanTag={certificatesAllowed ? undefined : 'Growth'}
          />
          <ChoiceCard
            icon={<CertUploadIcon />}
            title="Upload Custom Certificate"
            description={pdfName ?? 'Upload your own design for this program'}
            selected={choice === 'custom_upload'}
            onClick={() => setChoice('custom_upload')}
            lockedPlanTag={customAllowed ? undefined : 'Pro'}
          />
        </div>

        {choice === 'custom_upload' && (
          <div className="rounded-2xl border border-border-primary/20 px-5 py-4">
            {pdfKey ? (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-9 h-9 rounded-lg bg-error-500/10 inline-flex items-center justify-center">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"
                        stroke={palette.error['500']}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <polyline
                        points="14,2 14,8 20,8"
                        stroke={palette.error['500']}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <div className="min-w-0">
                    <p
                      className="text-sm font-semibold text-text-primary truncate"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      {pdfName}
                    </p>
                    <p className="text-xs text-text-secondary">PDF · uploaded</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemovePdf}
                  className="text-xs font-semibold text-error-500 hover:underline active:scale-[0.96] transition-[opacity,transform] duration-150"
                >
                  Remove
                </button>
              </div>
            ) : (
              <DropZone
                onFilesSelected={handlePdfSelected}
                accept="application/pdf"
                maxSizeMB={MAX_PDF_SIZE_MB}
                label={uploading ? `Uploading… ${uploadProgress}%` : 'Drop or select certificate PDF'}
              />
            )}
            {uploadError && (
              <p role="alert" className="text-xs text-error-500 mt-2">
                {uploadError}
              </p>
            )}
          </div>
        )}
      </div>
    </StepCard>
  );
}
